import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TransportError, ZimbraError, type ChangeNotification, type ZimbraClient } from '@zimbra-webif/sdk';
import { makeClient } from './client';
import type { Session } from './session';

/**
 * True when a request failed because the session is gone: the BFF answered
 * 401 (its cookie lapsed) or Zimbra itself rejected the token (AUTH_EXPIRED /
 * AUTH_REQUIRED fault, e.g. after zimbraAuthTokenLifetime). Anything else is a
 * normal error and must NOT log the user out.
 */
export function isAuthLost(err: unknown): boolean {
  if (err instanceof TransportError) return err.status === 401;
  if (err instanceof ZimbraError) return err.isAuthExpired;
  return false;
}

interface ClientContextValue {
  client: ZimbraClient;
  session: Session;
  /**
   * Pause the change-notification refresh during a bulk operation (e.g. moving
   * thousands of mails): changes are coalesced and refreshed ONCE when the last
   * holder releases. Returns the release function.
   */
  holdRefresh: () => () => void;
}

const ClientContext = createContext<ClientContextValue | null>(null);

export function useClient(): ClientContextValue {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error('useClient must be used within <ClientProvider>');
  return ctx;
}

/**
 * Provides the SDK client + a React Query cache, and runs the real-time
 * notification loop: a single NoOp long-poll whose `<notify>` blocks invalidate
 * the affected queries, so the mailbox auto-refreshes with no manual reload.
 * This is the capability the old zm-x-web preview never had.
 *
 * `onAuthLost` fires once when any query, mutation or the long-poll sees the
 * session is gone (see `isAuthLost`); the owner drops the session so the Login
 * screen replaces a silently dead UI. Without this an expired tab kept its stale
 * cache, every click failed quietly and the loop hammered the BFF every 5s.
 */
export function ClientProvider({
  session,
  onAuthLost,
  children,
}: {
  session: Session;
  onAuthLost: () => void;
  children: ReactNode;
}) {
  const client = useMemo(() => makeClient(session.csrfToken), [session.csrfToken]);

  // latest callback + once-only guard, so cache callbacks never go stale
  const authLostRef = useRef(onAuthLost);
  authLostRef.current = onAuthLost;
  const fired = useRef(false);
  const handleError = useMemo(
    () => (err: unknown) => {
      if (!isAuthLost(err) || fired.current) return;
      fired.current = true;
      authLostRef.current();
    },
    [],
  );

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: handleError }),
        mutationCache: new MutationCache({ onError: handleError }),
        defaultOptions: {
          queries: {
            staleTime: 15_000,
            refetchOnWindowFocus: false,
            // one retry for transient errors; none once the session is gone
            retry: (count, err) => count < 1 && !isAuthLost(err),
          },
        },
      }),
    [handleError],
  );

  // bulk-operation hold: while > 0, notifications only mark a refresh as pending
  const holds = useRef(0);
  const pending = useRef(false);
  const refreshAll = useMemo(
    () => () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      void queryClient.invalidateQueries({ queryKey: ['conversation'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
    [queryClient],
  );
  const holdRefresh = useMemo(
    () => () => {
      holds.current++;
      let released = false;
      return () => {
        if (released) return;
        released = true;
        holds.current = Math.max(0, holds.current - 1);
        if (holds.current === 0 && pending.current) {
          pending.current = false;
          refreshAll();
        }
      };
    },
    [refreshAll],
  );

  useEffect(() => {
    const invalidate = (_n: ChangeNotification) => {
      if (holds.current > 0) {
        pending.current = true;
        return;
      }
      // Broad-but-correct: any change refreshes the list (conversation AND message
      // views), the open thread, and folder counts — so new mail appears live.
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      void queryClient.invalidateQueries({ queryKey: ['conversation'] });
      void queryClient.invalidateQueries({ queryKey: ['folders'] });
    };
    const off = client.notifications.on(invalidate);
    const loop = client.sync.startNotificationLoop({ timeout: 30_000, onError: handleError });
    return () => {
      off();
      loop.stop();
    };
  }, [client, queryClient, handleError]);

  const value = useMemo(() => ({ client, session, holdRefresh }), [client, session, holdRefresh]);

  return (
    <ClientContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ClientContext.Provider>
  );
}
